import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
// ==========================================================================
// ✅ ASYNC THUNKS
// ==========================================================================
export const fetchAccountPerformanceTracker = createAsyncThunk("accountPerformanceTracker/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/account-performance-tracker/list", payload, {
            Authorization: token || "",
        });
        if (response && "data" in response && response?.data) {
            const responseData = response.data;
            if (responseData?.success) {
                return responseData?.data;
            }
            else {
                return rejectWithValue(responseData?.message || "Failed to fetch account performance tracker");
            }
        }
        else {
            return rejectWithValue("Failed to fetch account performance tracker");
        }
    }
    catch (error) {
        console.error("Account Performance Tracker API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching account performance tracker");
    }
});
// ==========================================================================
// ✅ INITIAL STATE
// ==========================================================================
const initialState = {
    data: null,
    loading: false,
    error: null,
    client_id: "",
    month: "",
};
import { toast } from "../common_components/Toast/ToastUtils";
export const savePerformanceTracker = createAsyncThunk('accountPerformanceTracker/save', async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall('POST', '/api/account-performance-tracker/save', payload, { Authorization: token || '' });
        if (response && 'data' in response && response?.data) {
            const responseData = response.data;
            if (responseData?.success) {
                toast.success('Modifier changed successfully');
                return responseData?.data;
            }
            toast.error(responseData?.message || 'Save failed');
            return rejectWithValue(responseData?.message || 'Save failed');
        }
        toast.error('Failed to save. Please try again.');
        return rejectWithValue('Failed to save. Please try again.');
    }
    catch (error) {
        toast.error(error?.response?.data?.message ||
            error.message ||
            'Error saving performance tracker');
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            'Error saving performance tracker');
    }
});
export const recalculatePerformanceTracker = createAsyncThunk('accountPerformanceTracker/recalculate', async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall('POST', '/api/account-performance-tracker/recalculate', payload, { Authorization: token || '' });
        if (response && 'data' in response && response?.data) {
            const responseData = response.data;
            if (responseData?.success) {
                toast.success('Projections recalculated successfully');
                dispatch(fetchAccountPerformanceTracker({ client_id: payload.client_id, month: payload.month }));
                return responseData?.data;
            }
            toast.error(responseData?.message || 'Recalculation failed');
            return rejectWithValue(responseData?.message || 'Recalculation failed');
        }
        toast.error('Failed to recalculate. Please try again.');
        return rejectWithValue('Failed to recalculate. Please try again.');
    }
    catch (error) {
        toast.error(error?.response?.data?.message ||
            error.message ||
            'Error recalculating projections');
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            'Error recalculating projections');
    }
});
// ==========================================================================
// ✅ SLICE
// ==========================================================================
const accountPerformanceTrackerSlice = createSlice({
    name: "accountPerformanceTracker",
    initialState,
    reducers: {
        setTrackerClientId: (state, action) => {
            state.client_id = action.payload;
        },
        setTrackerMonth: (state, action) => {
            state.month = action.payload;
        },
        clearTrackerData: (state) => {
            state.data = null;
            state.error = null;
        },
        clearTrackerError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAccountPerformanceTracker.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAccountPerformanceTracker.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
            state.error = null;
            state.client_id = action.meta.arg.client_id; // ✅ ADD
            state.month = action.meta.arg.month; // ✅ ADD
        })
            .addCase(fetchAccountPerformanceTracker.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch account performance tracker";
        });
    },
});
// ==========================================================================
// ✅ EXPORTS
// ==========================================================================
export const { setTrackerClientId, setTrackerMonth, clearTrackerData, clearTrackerError, } = accountPerformanceTrackerSlice.actions;
// ==========================================================================
// ✅ SELECTORS
// ==========================================================================
export const selectTrackerData = (state) => state.accountPerformanceTracker.data;
export const selectTrackerLoading = (state) => state.accountPerformanceTracker.loading;
export const selectTrackerError = (state) => state.accountPerformanceTracker.error;
export const selectTrackerClientId = (state) => state.accountPerformanceTracker.client_id;
export const selectTrackerMonth = (state) => state.accountPerformanceTracker.month;
export const selectTrackerFormulas = (state) => state.accountPerformanceTracker.data?.formulas ?? null;
export const selectSavedModifiers = (state) => state.accountPerformanceTracker.data?.savedModifiers ?? {};
export const selectSavedProjections = (state) => state.accountPerformanceTracker.data?.savedProjections ?? {};
export const selectTrackerLastUpdated = (state) => state.accountPerformanceTracker.data?.last_updated ?? null;
export default accountPerformanceTrackerSlice.reducer;
